import { AlertTriangle, X } from 'lucide-react'
import type { ApiKey } from './types'

interface DeleteApiKeyModalProps {
  apiKey: ApiKey | null
  isDeleting: boolean
  onCancel: () => void
  onConfirm: () => void
}

/** API 키 삭제 확인 */
export default function DeleteApiKeyModal({
  apiKey,
  isDeleting,
  onCancel,
  onConfirm,
}: DeleteApiKeyModalProps) {
  if (!apiKey) {
    return null
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="m-4 w-full max-w-md rounded-lg bg-white shadow-xl dark:bg-gray-800">
        <div className="flex items-center justify-between border-b border-gray-200 p-6 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            API 키 삭제 확인
          </h2>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          <div className="mb-4 flex items-center">
            <div className="flex-shrink-0">
              <AlertTriangle className="h-12 w-12 text-red-500" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                정말로 삭제하시겠습니까?
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                이 작업은 되돌릴 수 없습니다.
              </p>
            </div>
          </div>

          <div className="rounded-lg bg-red-50 p-4 dark:bg-red-900/20">
            <h4 className="mb-2 font-medium text-gray-900 dark:text-white">
              삭제할 API 키
            </h4>
            <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
              <div>
                <strong>이름:</strong> {apiKey.name}
              </div>
              <div>
                <strong>사용자:</strong> {apiKey.userId.email}
              </div>
              <div>
                <strong>키 ID:</strong>{' '}
                <code className="font-mono">{apiKey.keyId}</code>
              </div>
            </div>
          </div>

          <div className="mt-4 rounded-lg bg-yellow-50 p-3 dark:bg-yellow-900/20">
            <p className="text-sm text-yellow-800 dark:text-yellow-200">
              ⚠️ <strong>주의:</strong> API 키를 삭제하면 해당 키를 사용하는
              모든 애플리케이션이 더 이상 작동하지 않습니다. 관련 사용량 기록도
              함께 삭제됩니다.
            </p>
          </div>
        </div>

        <div className="flex justify-end space-x-3 border-t border-gray-200 p-6 dark:border-gray-700">
          <button
            onClick={onCancel}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
          >
            취소
          </button>
          <button
            onClick={onConfirm}
            disabled={isDeleting}
            className="rounded-lg border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isDeleting ? '삭제 중...' : '삭제'}
          </button>
        </div>
      </div>
    </div>
  )
}
